import { CheckoutState, siteStorageName } from "@crunchit/types";
import { clearAllLocalStorage } from "@crunchit/utilities";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAppSelector, appActions } from "store/app";
import { useCheckoutSelector, checkoutActions } from "store/checkout";
import { basketActions } from "store/basket";
import { useCustomDispatch } from "store/useStore";

import useFinalOrder from "hooks/useFinalOrder";

import PageTitle from "components/ui/PageTitle";
import OrderList from "components/basket/OrderList/OrderList";
import RestaurantInfo from "components/ui/RestaurantInfo";
import ItemizedTotal from "components/basket/totals/ItemizedTotal";

import "./Confirmation.scss";

export default function Confirmation() {
  const { t } = useTranslation();
  const { appIsInitialized, token, productionUnit } = useAppSelector();
  const { checkout } = useCheckoutSelector();
  const dispatch = useCustomDispatch();
  const redirect = useNavigate();

  let [orderId, setOrderId] = useState<string>();

  const { orderError, orderProperties } = useFinalOrder(orderId);

  useEffect(() => {
    if (appIsInitialized && !orderProperties) {
      if (checkout && checkout.id) {
        if (checkout.state === CheckoutState.DONE && checkout.orderId) {
          setOrderId(checkout.orderId);
        } else {
          // Checkout not successful
          dispatch(appActions.setError({ message: "Checkout failed", error: checkout }));
        }
      } else {
        // No checkout in the session - likely a navigation error
        redirect("/");
      }
    }
  }, [appIsInitialized, token, checkout, orderProperties, dispatch, redirect]);

  useEffect(() => {
    if (orderError) {
      dispatch(appActions.setError({ message: "Order failed", error: orderError }));
    }
  }, [orderError, dispatch]);

  // Delete basket and checkout in localStorage and redux after fetching order successfully
  useEffect(() => {
    if (orderProperties) {
      dispatch(checkoutActions.setCheckout(null));
      dispatch(basketActions.setBasket(null));
      clearAllLocalStorage(siteStorageName);
    }
  }, [orderProperties, dispatch]);

  let timeText = "";

  if (orderProperties) {
    const deliveryTypeText = orderProperties.deliveryType === "delivery" ? t("checkout:DeliveryMethod.DeliveryTitle") : t("checkout:DeliveryMethod.PickupTitle");
    timeText = t("confirmation:Timestamp", { deliveryType: deliveryTypeText.toLowerCase(), date: orderProperties.date, time: orderProperties.time });
  }

  let receiptMessageText = "";

  if (orderProperties && orderProperties.receiptMessageType) {
    const messageTypeText = orderProperties.receiptMessageType === "SMS" ? t("common:Communication.SMS") : t("common:Communication.Email");
    receiptMessageText = t("confirmation:ReceiptMessage", { messageType: messageTypeText.toLowerCase() });
  }

  return (
    <>
      <PageTitle title={t("pagetitles:ConfirmationPage.Title")} />

      {orderProperties && (
        <div className="content-body confirmation-content">
          <p className="status-page-text confirmation-text-title">{t("pagetitles:ConfirmationPage.Subtitle")}</p>

          {productionUnit && <RestaurantInfo productionUnit={productionUnit} />}

          <p className="status-page-text">
            {t("confirmation:OrderNumber")}: {orderProperties.receiptNumber}
          </p>

          <p className="status-page-text timestamp">{timeText}</p>

          {receiptMessageText !== "" && (
            <p className="status-page-text email-confirmation-text">
              <strong>{receiptMessageText}</strong>
            </p>
          )}

          {orderProperties.products.length > 0 && (
            <div className="order-section">
              <h2 className="order-title">{t("cart:CartTitle")}</h2>

              <div className="orderlist">
                <OrderList isFinal products={orderProperties.products} />

                <ItemizedTotal isDiscountEnabled={orderProperties.discountAmount > 0} orderTotal={orderProperties.orderTotal} deliveryFee={orderProperties.deliveryFee} discountAmount={orderProperties.discountAmount} payments={orderProperties.payments} />
              </div>
            </div>
          )}
          <button className="button" onClick={() => redirect("/")}>
            {t("confirmation:ReturnToStartButton")}
          </button>
        </div>
      )}
    </>
  );
}
